// Bridge between the React web app and the native Android (Capacitor) shell.
// Everything degrades gracefully on the web (no native => web behaviour).
//
// The native shell injects `window.ParasNative` (a thin JS interface) and/or
// exposes Capacitor at `window.Capacitor`. We never hard-depend on Capacitor so
// the web build stays clean.

export function isNativeApp() {
  if (typeof window === "undefined") return false;
  if (window.ParasNative) return true;
  const cap = window.Capacitor;
  return !!(cap && (cap.isNativePlatform ? cap.isNativePlatform() : cap.isNative));
}

export function platform() {
  if (typeof window === "undefined") return "web";
  const cap = window.Capacitor;
  if (cap && cap.getPlatform) return cap.getPlatform(); // 'android' | 'ios' | 'web'
  return isNativeApp() ? "android" : "web";
}

// Show a native AdMob interstitial. Resolves when the ad is closed (or immediately
// if no native bridge / ads disabled). The web falls back to the AdSense modal.
export function showInterstitialAd() {
  return new Promise((resolve) => {
    if (isNativeApp() && window.ParasNative && window.ParasNative.showInterstitial) {
      try {
        // native calls window.__onAdClosed() when the ad is dismissed
        window.__onAdClosed = () => { window.__onAdClosed = null; resolve(true); };
        window.ParasNative.showInterstitial();
        // safety timeout so the UI never gets stuck if native never calls back
        setTimeout(() => { if (window.__onAdClosed) { window.__onAdClosed = null; resolve(true); } }, 8000);
      } catch { resolve(false); }
    } else {
      resolve(false); // web: caller shows the AdSense <AdInterstitial/> modal instead
    }
  });
}

// Open Razorpay checkout. In the native app this hands off to the Razorpay
// Android SDK (UPI intent apps NOT blocked). On web it loads checkout.js.
// `options` must include: order_id, amount (paise), currency, key(publishable), name, prefill{}.
// Resolves { razorpay_payment_id, razorpay_order_id, razorpay_signature } on success.
export function openRazorpayCheckout(options) {
  return new Promise((resolve, reject) => {
    if (!options || !options.key) {
      reject(new Error("Payment key missing — please refresh and try again."));
      return;
    }
    if (isNativeApp() && window.ParasNative && window.ParasNative.openRazorpay) {
      window.__onRazorpaySuccess = (payloadJson) => {
        window.__onRazorpaySuccess = null; window.__onRazorpayError = null;
        try { resolve(typeof payloadJson === "string" ? JSON.parse(payloadJson) : payloadJson); }
        catch { resolve(payloadJson); }
      };
      window.__onRazorpayError = (errJson) => {
        window.__onRazorpaySuccess = null; window.__onRazorpayError = null;
        reject(typeof errJson === "string" ? JSON.parse(errJson) : errJson);
      };
      window.ParasNative.openRazorpay(JSON.stringify(options));
      return;
    }
    // Web checkout.js
    const launch = () => {
      const rzp = new window.Razorpay({
        ...options,
        handler: (resp) => resolve(resp),
        modal: { ondismiss: () => reject(new Error("Checkout dismissed")) },
      });
      rzp.open();
    };
    if (window.Razorpay) return launch();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = launch;
    s.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(s);
  });
}
